import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Copy, Check, Tag, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { getHomepageCouponDisplay } from "@/lib/marketing/homepage-coupon.functions";
import { useCouponRotation } from "@/lib/marketing/coupon-rotation";
import { formatPaise } from "@/lib/format/money";
import { Button } from "@/components/ui/button";
import { CouponNavigationDots } from "./CouponNavigationDots";

function promoMessage(c: { type: string; value: number; name: string }) {
  const discount = c.type === "percentage" ? `${c.value}% off` : `${formatPaise(c.value)} off`;
  return `${c.name} — ${discount}`;
}

export function HomepageCouponStrip() {
  const fetchDisplay = useServerFn(getHomepageCouponDisplay);
  const { data } = useQuery({
    queryKey: ["homepage", "coupon-display"],
    queryFn: () => fetchDisplay(),
    staleTime: 60_000,
  });

  const [copied, setCopied] = useState(false);

  const coupons = data?.coupons ?? [];
  const rotation = data?.mode === "rotation" && coupons.length > 1;
  const { index, prev, next, select } = useCouponRotation(coupons.length, rotation);

  if (!data?.enabled || coupons.length === 0) return null;

  const coupon = coupons[index];
  const showNav = coupons.length > 1;

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(coupon.code);
      setCopied(true);
      toast.success(`Code ${coupon.code} copied`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy the code");
    }
  };

  const navButton =
    "absolute top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-full border border-primary/30 bg-background text-primary hover:bg-secondary disabled:opacity-40";

  return (
    <section className="relative border-y border-border/60 bg-secondary/40">
      {showNav && (
        <Button variant="ghost" size="icon" type="button" onClick={prev} aria-label="Previous coupon" className={`${navButton} left-2 md:left-6`}>
          <ChevronLeft className="h-5 w-5" aria-hidden />
        </Button>
      )}
      {showNav && (
        <Button variant="ghost" size="icon" type="button" onClick={next} aria-label="Next coupon" className={`${navButton} right-2 md:right-6`}>
          <ChevronRight className="h-5 w-5" aria-hidden />
        </Button>
      )}
      <div className="mx-auto flex max-w-4xl flex-col items-center gap-3 px-12 py-8 text-center md:py-10">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
          <Tag className="h-4 w-4 text-primary" aria-hidden />
          Special offer
        </span>
        <h2 className="font-display text-2xl font-medium md:text-3xl">{promoMessage(coupon)}</h2>
        {coupon.description && (
          <p className="max-w-xl text-sm leading-relaxed text-muted-foreground md:text-base">
            {coupon.description}
          </p>
        )}
        <Button variant="ghost"
          type="button"
          onClick={() => void copyCode()}
          className="mt-1 inline-flex items-center gap-2 rounded-full border border-primary/40 bg-background px-5 py-2 font-mono text-sm font-semibold text-primary hover:bg-secondary"
          aria-label={`Copy coupon code ${coupon.code}`}
        >
          {coupon.code}
          {copied ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
        </Button>
        <CouponNavigationDots codes={coupons.map((c) => c.code)} index={index} onSelect={select} />
        {showNav && (
          <span className="text-xs text-muted-foreground" aria-hidden>
            {index + 1}/{coupons.length}
          </span>
        )}
      </div>
    </section>
  );
}
